<template>
  <div class="admin-auth-root min-vh-100 d-flex flex-column justify-content-between p-3 p-md-4">
    <!-- Top Bar -->
    <div class="d-flex justify-content-between align-items-center mb-2 px-1">
      <div class="d-flex align-items-center gap-2">
        <span class="system-status-pill">
          <span class="status-dot"></span>
          <span>{{ $currentLocale === 'id' ? 'Manajemen Perusahaan' : 'Enterprise Management' }}</span>
        </span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <div class="lang-switch-group">
          <button
            type="button"
            class="lang-btn"
            :class="{ active: $currentLocale === 'en' }"
            @click="$setLocale('en')"
          >
            EN
          </button>
          <span class="lang-divider">/</span>
          <button
            type="button"
            class="lang-btn"
            :class="{ active: $currentLocale === 'id' }"
            @click="$setLocale('id')"
          >
            ID
          </button>
        </div>

        <router-link to="/login" class="player-link">
          ← {{ $t('auth.backToLogin') }}
        </router-link>
      </div>
    </div>

    <!-- Centered Auth Card -->
    <div class="d-flex align-items-center justify-content-center flex-grow-1 py-3">
      <div class="login-box w-100">
        <div class="login-panel">
          <!-- Header -->
          <div class="text-center mb-4">
            <div class="brand-symbol mb-3">
              <img
                src="../assets/images/logo-big.svg"
                alt="Gamiteam"
                class="brand-logo"
              />
            </div>
            <h1 class="panel-heading">
              {{ hasResetToken ? $t('auth.resetPasswordTitle') : $t('auth.resetPasswordModalTitle') }}
            </h1>
            <p class="panel-subheading">
              {{ hasResetToken ? $t('profile.resetPasswordSubtitle') : $t('auth.resetPasswordModalDesc') }}
            </p>
          </div>

          <!-- Request Reset Link Form (when no token in query) -->
          <form v-if="!hasResetToken" @submit.prevent="requestResetLink" autocomplete="off">
            <div class="form-group mb-4">
              <label class="form-label" for="resetEmail">
                {{ $t('auth.registeredEmail') }}
              </label>
              <input
                type="email"
                id="resetEmail"
                class="form-control-input"
                v-model="userStore.email"
                :placeholder="$t('auth.emailPlaceholder')"
                required
                autocomplete="email"
              />
            </div>

            <button
              type="submit"
              class="primary-submit-btn mb-3"
              :disabled="loading || !userStore.email"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <span>{{ loading ? $t('auth.sending') : $t('auth.sendResetLink') }}</span>
            </button>

            <div class="text-center mt-3">
              <router-link to="/login" class="forgot-link">
                ← {{ $t('auth.backToLogin') }}
              </router-link>
            </div>
          </form>

          <!-- Set New Password Form (when token is present) -->
          <form v-else class="form-login" @submit.prevent="submit" autocomplete="off">
            <div class="form-group mb-3">
              <label class="form-label" for="resetConfirmedEmail">{{ $t('auth.registeredEmail') }}</label>
              <input
                type="email"
                id="resetConfirmedEmail"
                class="form-control-input"
                v-model="userStore.email"
                :placeholder="$t('auth.emailPlaceholder')"
                required
                autocomplete="email"
              />
            </div>

            <div class="form-group mb-3">
              <label class="form-label" for="newPassword">{{ $t('profile.newPassword') }}</label>
              <div class="password-input-wrapper">
                <input
                  :type="showNew ? 'text' : 'password'"
                  class="form-control-input password-field"
                  id="newPassword"
                  v-model="userStore.new_password"
                  :placeholder="$t('profile.newPasswordPlaceholder')"
                  required
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showNew = !showNew"
                  :aria-label="showNew ? $t('profile.hidePassword') : $t('profile.showPassword')"
                  :title="showNew ? $t('profile.hidePassword') : $t('profile.showPassword')"
                >
                  <i :class="showNew ? 'mdi mdi-eye-off text-warning' : 'mdi mdi-eye text-muted'" aria-hidden="true"></i>
                </button>
              </div>
            </div>

            <div class="form-group mb-4">
              <label class="form-label" for="confirmNewPassword">{{ $t('profile.confirmNewPassword') }}</label>
              <div class="password-input-wrapper">
                <input
                  :type="showConfirm ? 'text' : 'password'"
                  class="form-control-input password-field"
                  id="confirmNewPassword"
                  v-model="userStore.confirm_new_password"
                  :placeholder="$t('profile.confirmNewPasswordPlaceholder')"
                  required
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  @click="showConfirm = !showConfirm"
                  :aria-label="showConfirm ? $t('profile.hidePassword') : $t('profile.showPassword')"
                  :title="showConfirm ? $t('profile.hidePassword') : $t('profile.showPassword')"
                >
                  <i :class="showConfirm ? 'mdi mdi-eye-off text-warning' : 'mdi mdi-eye text-muted'" aria-hidden="true"></i>
                </button>
              </div>
              <div
                v-if="
                  userStore.new_password &&
                  userStore.confirm_new_password &&
                  userStore.new_password !== userStore.confirm_new_password
                "
                class="text-danger mt-1 small"
                role="alert"
              >
                {{ $t('register.passwordMismatch') }}
              </div>
            </div>

            <button
              type="submit"
              class="primary-submit-btn mb-3"
              :disabled="
                loading ||
                !userStore.email ||
                !userStore.new_password ||
                userStore.new_password !== userStore.confirm_new_password
              "
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <span>{{ $t('auth.resetPasswordTitle') }}</span>
            </button>

            <div class="text-center mt-3">
              <router-link to="/login" class="forgot-link">
                ← {{ $t('auth.backToLogin') }}
              </router-link>
            </div>
          </form>

          <!-- Security Badge -->
          <div class="security-badge-footer">
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            <span>{{ $currentLocale === 'id' ? 'Keamanan Sesi Terverifikasi • Isolasi Multi-Tenant' : 'Verified Session Security • Multi-Tenant Isolation' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="text-center admin-page-footer py-1">
      <span>© {{ new Date().getFullYear() }} {{ $currentLocale === 'id' ? 'Portal Admin Gamiteam' : 'Gamiteam Admin Portal' }}</span>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "../config/config";
import Swal from "sweetalert2";
import { forgotPasswordAPI } from "../services/authService";

export default {
  name: "ForgotPassword",
  data: () => ({
    userStore: {
      email: "",
      token: "",
      new_password: "",
      confirm_new_password: "",
    },
    showNew: false,
    showConfirm: false,
    config,
    loading: false,
  }),
  computed: {
    hasResetToken() {
      return Boolean(this.userStore.token || this.$route?.query?.token);
    },
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler(q) {
        if (q) {
          this.userStore.email = q.email || this.userStore.email || "";
          this.userStore.token = q.token || "";
        }
      },
    },
  },
  methods: {
    async requestResetLink() {
      if (!this.userStore.email) return;
      this.loading = true;
      try {
        const response = await forgotPasswordAPI(this.userStore.email);
        await Swal.fire({
          icon: "success",
          title: this.$t("common.success"),
          text:
            response?.message ||
            (this.$currentLocale === "id"
              ? `Tautan pemulihan kata sandi telah dikirim ke ${this.userStore.email}.`
              : `Password reset instructions sent to ${this.userStore.email}.`),
          confirmButtonColor: "#1e293b",
        });
        this.$router.push("/login").catch(() => {});
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text:
            err.response?.data?.message ||
            (this.$currentLocale === "id"
              ? "Email tidak ditemukan atau gagal mengirim tautan pemulihan."
              : "No account found with this email address."),
          confirmButtonColor: "#ef4444",
        });
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      const resetToken = this.userStore.token || this.$route?.query?.token || "";
      if (!resetToken || !this.userStore.new_password || this.userStore.new_password !== this.userStore.confirm_new_password) {
        return;
      }
      this.loading = true;
      const API_URL = config.API_URL;

      try {
        const res = await axios.post(`${API_URL}auth/reset-password`, {
          email: this.userStore.email || "",
          token: resetToken,
          new_password: this.userStore.new_password,
          password: this.userStore.new_password,
          password_confirmation: this.userStore.confirm_new_password,
        });

        await Swal.fire({
          icon: "success",
          title: this.$t("common.success"),
          text:
            res?.data?.message ||
            (this.$currentLocale === "id"
              ? "Kata sandi berhasil diperbarui. Silakan masuk."
              : "Password updated successfully. Please sign in."),
          confirmButtonColor: "#0090e7",
        });

        this.$router.push("/login").catch(() => {});
      } catch (err) {
        const errorsObj = err.response?.data?.errors;
        const firstFieldErr =
          errorsObj && typeof errorsObj === "object"
            ? Object.values(errorsObj).flat()[0]
            : null;
        const msg =
          firstFieldErr ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          (this.$currentLocale === "id"
            ? "Gagal mengatur ulang kata sandi. Pastikan tautan reset masih berlaku."
            : "Failed to reset password. Please check your reset link or try again.");
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: msg,
          confirmButtonColor: "#ef4444",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-auth-root {
  background-color: #08090d;
  background-image:
    radial-gradient(at 50% 0%, rgba(30, 41, 59, 0.4) 0px, transparent 65%),
    radial-gradient(at 100% 100%, rgba(15, 23, 42, 0.3) 0px, transparent 50%);
  color: #f1f5f9;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.system-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  color: #94a3b8;
}

.status-dot {
  width: 6px;
  height: 6px;
  background-color: #6366f1;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(99, 102, 241, 0.6);
}

.lang-switch-group {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 9999px;
  padding: 2px 6px;
}

.lang-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  cursor: pointer;
  border-radius: 9999px;
  transition: all 0.15s ease;
}

.lang-btn.active {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.14);
}

.lang-divider {
  font-size: 10px;
  color: #64748b;
}

.player-link {
  color: #cbd5e1;
  font-size: 12px;
  text-decoration: none;
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.15s ease;
}

.player-link:hover {
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.07);
}

.login-box {
  max-width: 420px;
}

.login-panel {
  background: rgba(15, 17, 26, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 34px 30px;
  box-shadow: 0 24px 48px -18px rgba(0, 0, 0, 0.8);
}

.brand-symbol {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.brand-logo {
  height: 36px;
  width: auto;
}

.panel-heading {
  font-size: 19px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
  letter-spacing: -0.02em;
}

.panel-subheading {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #cbd5e1;
  margin-bottom: 6px;
}

.form-control-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 9px 12px;
  color: #ffffff;
  font-size: 13px;
  transition: all 0.15s ease;
}

.form-control-input:focus-visible,
.lang-btn:focus-visible,
.password-toggle-btn:focus-visible,
.primary-submit-btn:focus-visible,
.player-link:focus-visible,
.forgot-link:focus-visible {
  outline: 2px solid #818cf8;
  outline-offset: 2px;
}

.form-control-input:focus {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(99, 102, 241, 0.6);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

.password-input-wrapper {
  position: relative;
}

.password-field {
  padding-right: 44px;
}

.password-toggle-btn {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  padding: 0;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease;
}

.password-toggle-btn:hover {
  color: #f8fafc;
}

.forgot-link {
  font-size: 12px;
  color: #a5b4fc;
  text-decoration: none;
  transition: color 0.15s ease;
}

.forgot-link:hover {
  color: #c7d2fe;
  text-decoration: underline;
}

.primary-submit-btn {
  width: 100%;
  background: #ffffff;
  color: #090a0f;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-submit-btn:hover:not(:disabled) {
  background: #f1f5f9;
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.15);
}

.primary-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.security-badge-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 10px;
  color: #94a3b8;
}

.admin-page-footer {
  font-size: 11px;
  color: #94a3b8;
}
</style>

